import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { X, HeartHandshake, Phone, MessageCircle } from "lucide-react";
import {
  getTrustedContact,
  saveTrustedContact,
  clearTrustedContact,
  telHref,
  smsHref,
} from "@/lib/trustedContact";

// A discreet home-screen button — one pre-set person, one tap to call or
// text. Saved only on this device. Modeled after the other overlays.
// by David Hunt
export const TrustedContact = ({ dimmed = false }) => {
  const [open, setOpen] = useState(false);
  const [contact, setContact] = useState(() => getTrustedContact());
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");

  useEffect(() => {
    if (open) {
      const c = getTrustedContact();
      setContact(c);
      setEditing(!c);
      setName(c?.name || "");
      setPhone(c?.phone || "");
    }
  }, [open]);

  const handleSave = (e) => {
    e.preventDefault();
    const n = name.trim();
    const p = phone.trim();
    if (!n || !p) return;
    saveTrustedContact(n, p);
    setContact({ name: n, phone: p });
    setEditing(false);
  };

  const handleRemove = () => {
    clearTrustedContact();
    setContact(null);
    setName("");
    setPhone("");
    setEditing(true);
  };

  const inputCls =
    "w-full rounded-xl bg-white/5 border border-white/10 backdrop-blur-xl px-4 py-3 text-white/85 placeholder-white/30 text-sm focus:outline-none focus:border-white/30 transition-colors duration-500";

  return (
    <>
      <button
        onClick={() => {
          toast.dismiss();
          setOpen(true);
        }}
        className={`fixed w-10 h-10 rounded-full flex items-center justify-center backdrop-blur-xl bg-white/5 border border-white/10 text-white/50 hover:bg-white/10 hover:text-white/85 transition-opacity duration-[1500ms] ease-in-out z-10 ${
          dimmed ? "opacity-0 pointer-events-none" : "opacity-100"
        }`}
        style={{
          bottom: "calc(env(safe-area-inset-bottom) + 1rem)",
          left: "calc(env(safe-area-inset-left) + 1rem)",
        }}
        data-testid="trusted-contact-btn"
        aria-label="Reach someone you trust"
        title="someone you trust"
      >
        <HeartHandshake size={16} strokeWidth={1.5} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            key="trusted-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8, ease: "easeInOut" }}
            className="fixed inset-0 z-30 overflow-y-auto overscroll-contain"
            data-testid="trusted-contact-overlay"
          >
            <div
              className="fixed inset-0 bg-black/45 backdrop-blur-2xl pointer-events-none"
              aria-hidden="true"
            />

            <button
              onClick={() => setOpen(false)}
              className="fixed w-10 h-10 rounded-full flex items-center justify-center bg-white/5 border border-white/10 backdrop-blur-xl text-white/70 hover:bg-white/10 hover:text-white transition-colors duration-500 z-10"
              style={{
                top: "calc(env(safe-area-inset-top) + 1rem)",
                right: "calc(env(safe-area-inset-right) + 1rem)",
              }}
              data-testid="trusted-close-btn"
              aria-label="Close"
            >
              <X size={18} strokeWidth={1.5} />
            </button>

            <div className="relative min-h-full flex flex-col items-center justify-center px-6 py-20">
              <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1.0, ease: "easeInOut", delay: 0.15 }}
                className="w-full max-w-sm text-center"
              >
                <h2 className="font-heading italic text-white/90 text-2xl sm:text-3xl">
                  someone you trust
                </h2>
                <p className="mt-2 text-xs text-white/40 tracking-wider uppercase">
                  saved only on this device
                </p>

                {editing || !contact ? (
                  <form onSubmit={handleSave} className="mt-10 space-y-4">
                    <p className="text-white/60 text-sm leading-relaxed">
                      pick one person who feels safe to reach. when things are
                      a lot, they'll be one tap away.
                    </p>
                    <input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="their name"
                      className={inputCls}
                      data-testid="trusted-name-input"
                      autoComplete="name"
                    />
                    <input
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="their phone number"
                      type="tel"
                      className={inputCls}
                      data-testid="trusted-phone-input"
                      autoComplete="tel"
                    />
                    <button
                      type="submit"
                      disabled={!name.trim() || !phone.trim()}
                      className="w-full rounded-full px-6 py-3 backdrop-blur-xl bg-white/10 border border-white/15 text-white/85 hover:bg-white/15 disabled:opacity-40 text-sm tracking-wide transition-colors duration-500"
                      data-testid="trusted-save-btn"
                    >
                      keep them close
                    </button>
                    {contact && (
                      <button
                        type="button"
                        onClick={() => setEditing(false)}
                        className="text-xs text-white/40 hover:text-white/70 transition-colors duration-500"
                        data-testid="trusted-cancel-edit-btn"
                      >
                        never mind
                      </button>
                    )}
                  </form>
                ) : (
                  <div className="mt-10 space-y-4">
                    <p
                      className="text-white/70 text-sm"
                      data-testid="trusted-contact-name"
                    >
                      {contact.name} is one tap away.
                    </p>
                    <a
                      href={telHref(contact.phone)}
                      className="flex items-center justify-center gap-3 w-full rounded-full px-6 py-4 backdrop-blur-xl bg-white/10 border border-white/15 text-white/90 hover:bg-white/15 text-base tracking-wide transition-colors duration-500"
                      data-testid="trusted-call-btn"
                    >
                      <Phone size={18} strokeWidth={1.5} />
                      call {contact.name}
                    </a>
                    <a
                      href={smsHref(contact.phone)}
                      className="flex items-center justify-center gap-3 w-full rounded-full px-6 py-4 backdrop-blur-xl bg-white/5 border border-white/10 text-white/80 hover:bg-white/10 text-base tracking-wide transition-colors duration-500"
                      data-testid="trusted-text-btn"
                    >
                      <MessageCircle size={18} strokeWidth={1.5} />
                      text {contact.name}
                    </a>
                    <div className="flex items-center justify-center gap-5 pt-2">
                      <button
                        onClick={() => setEditing(true)}
                        className="text-xs text-white/40 hover:text-white/70 transition-colors duration-500"
                        data-testid="trusted-edit-btn"
                      >
                        change
                      </button>
                      <button
                        onClick={handleRemove}
                        className="text-xs text-white/40 hover:text-white/70 transition-colors duration-500"
                        data-testid="trusted-remove-btn"
                      >
                        remove
                      </button>
                    </div>
                  </div>
                )}
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default TrustedContact;
